/**
 * Silensia Recovery — shared page-index navigation panel (new 2026-08-25,
 * scroll-lock corrected and closing animation added 2026-08-26).
 * Sitewide, dependency-free vanilla JS (no framework, no third-party
 * navigation library). Two triggers share one native <dialog>: the mobile
 * Menu trigger in the header (sitewide, <990px) and the Homepage-only
 * floating Explore control (snippets/silensia-page-index.liquid).
 *
 * Native <dialog> + showModal() gives real focus containment (background
 * inert, Tab cannot escape), native Escape-to-close, and top-layer
 * stacking for free -- deliberately chosen over a hand-rolled focus trap.
 *
 * Dispatches silensia:nav-panel-open / silensia:nav-panel-close on
 * document so assets/silensia-homepage-interactions.js can hide the
 * floating Contact CTA while the panel is open and restore it correctly
 * afterward, without either file importing the other.
 *
 * Scroll-lock correction (2026-08-26): html/body are permanently
 * overflow:hidden sitewide (Horizon's own base CSS, confirmed live before
 * this pass touched anything) -- .page-wrapper is this theme's actual
 * scrolling element. The previous lock toggled document.body.style.overflow,
 * which was a genuine no-op (body's computed overflow never changed), so
 * the background page stayed fully scrollable behind the "modal" panel.
 * Corrected to lock .page-wrapper itself, with its exact scroll position
 * saved and restored, and a defensive scrollbar-width compensation
 * (window.innerWidth - document.documentElement.clientWidth, applied once,
 * removed once, as a logical padding-inline-end so it also holds under
 * RTL) alongside snippets/silensia-brand-styles.liquid's own
 * `.page-wrapper { scrollbar-gutter: stable; }`, which already keeps the
 * scrollbar track permanently reserved regardless of overflow state -- so
 * in a browser that honours scrollbar-gutter, the JS compensation below
 * computes to 0px and is a true no-op; it exists only as the belt-and-braces
 * fallback for browsers or configurations where it does not.
 */
(function () {
  'use strict';

  var dialog = document.getElementById('silensia-page-index-dialog');
  if (!dialog) return;

  var links = dialog.querySelectorAll('[data-silensia-page-index-link]');
  var closeButton = dialog.querySelector('[data-silensia-page-index-close]');
  var lastTrigger = null;
  var isClosing = false;
  var isNavigating = false;

  var CLOSE_DURATION = 180; // Slightly faster than the panel's own 260ms entrance.

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  var scrollLockTarget = document.querySelector('.page-wrapper') || document.documentElement;
  var savedScrollTop = 0;
  var appliedScrollbarCompensation = false;

  function lockScroll() {
    savedScrollTop = scrollLockTarget.scrollTop;
    var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      scrollLockTarget.style.paddingInlineEnd = scrollbarWidth + 'px';
      appliedScrollbarCompensation = true;
    }
    scrollLockTarget.style.overflow = 'hidden';
  }

  // restorePosition is false only for a navigation close (see isNavigating
  // below) -- every dismissal route (Escape, backdrop, the close button)
  // still restores the exact position the panel opened from.
  function unlockScroll(restorePosition) {
    scrollLockTarget.style.overflow = '';
    if (appliedScrollbarCompensation) {
      scrollLockTarget.style.paddingInlineEnd = '';
      appliedScrollbarCompensation = false;
    }
    if (restorePosition) {
      scrollLockTarget.scrollTop = savedScrollTop;
    }
  }

  function setTriggersExpanded(expanded) {
    var triggers = document.querySelectorAll('[data-silensia-page-index-trigger]');
    for (var i = 0; i < triggers.length; i++) {
      triggers[i].setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }

  function openPanel(trigger) {
    if (dialog.open || isClosing) return;
    lastTrigger = trigger || null;
    lockScroll();
    dialog.showModal();
    setTriggersExpanded(true);
    // Dispatched before the panel's own opacity reveal starts (below), so
    // the floating Contact CTA's hide transition always has a head start
    // and its pointer-events:none applies immediately, well before the
    // panel becomes visually interactive.
    document.dispatchEvent(new CustomEvent('silensia:nav-panel-open'));

    if (prefersReducedMotion()) {
      dialog.setAttribute('data-silensia-page-index-state', 'visible');
    } else {
      // Two rAFs: the first lets the browser paint the dialog in its
      // resting (opacity:0, translateY(12px)) state; the second flips the
      // state attribute so the transition genuinely has a "before" frame
      // to animate from, rather than both states landing in the same paint.
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          dialog.setAttribute('data-silensia-page-index-state', 'visible');
        });
      });
    }

    if (closeButton) closeButton.focus();
  }

  // Animates the panel out (reversing its own entrance transition, at the
  // faster CLOSE_DURATION via .is-closing's shorter transition-duration —
  // see snippets/silensia-page-index.liquid), then calls the real native
  // dialog.close() once that animation has had time to finish. Every close
  // route (Escape, backdrop click, the close button, and selecting a
  // same-page link) funnels through this one function.
  function beginClose() {
    if (!dialog.open || isClosing) return;
    isClosing = true;
    dialog.classList.add('is-closing');
    dialog.removeAttribute('data-silensia-page-index-state');

    if (prefersReducedMotion()) {
      finishClose();
    } else {
      window.setTimeout(finishClose, CLOSE_DURATION);
    }
  }

  function finishClose() {
    isClosing = false;
    dialog.classList.remove('is-closing');
    if (dialog.open) dialog.close();
  }

  dialog.addEventListener('close', function () {
    unlockScroll(!isNavigating);
    setTriggersExpanded(false);
    document.dispatchEvent(new CustomEvent('silensia:nav-panel-close'));
    // Refocusing the trigger is correct after a dismissal (Escape,
    // backdrop, the close button) -- the visitor stayed put and the
    // trigger is exactly where focus should return. It is wrong after a
    // real navigation: focusAnchorTarget() (below, driven by the
    // 'hashchange' event the browser fires as part of the anchor's own
    // default action) already moves focus to the destination heading with
    // preventScroll:true, and calling lastTrigger.focus() here as well
    // would fight that, potentially reversing the scroll the anchor
    // navigation just performed.
    if (!isNavigating && lastTrigger && document.contains(lastTrigger)) {
      lastTrigger.focus();
    }
    lastTrigger = null;
    isNavigating = false;
  });

  // Escape fires 'cancel' before closing natively and instantly; intercept
  // it so Escape animates out through the same beginClose() path as every
  // other close route, instead of skipping straight to an unanimated close.
  dialog.addEventListener('cancel', function (event) {
    event.preventDefault();
    beginClose();
  });

  // Backdrop click: only the native <dialog> element itself receives the
  // click when the click lands outside .silensia-page-index__panel.
  dialog.addEventListener('click', function (event) {
    if (event.target === dialog) beginClose();
  });

  if (closeButton) {
    closeButton.addEventListener('click', beginClose);
  }

  // Root cause of the previous navigation bug (2026-08-25 diagnostic pass):
  // a link click used to route through beginClose(), the same ~180ms
  // animated-fade path Escape/backdrop/the close button still use below.
  // That path leaves scrollLockTarget locked (overflow:hidden) for the
  // full animation, and only then unconditionally restores the pre-open
  // scroll position. Confirmed live: the anchor's own default action (URL
  // hash update + scroll-into-view) fires essentially synchronously with
  // the click, so it landed while .page-wrapper -- the only real scroll
  // container; html/body are permanently overflow:hidden (Horizon's own
  // base CSS) -- was still locked, so the native scroll had nothing to
  // act on and silently did nothing; ~180ms later the delayed, always-on
  // scrollLockTarget.scrollTop = savedScrollTop then snapped the page
  // straight back to where the panel had opened from. That produced
  // exactly the reported symptom: a small bounce, then no real movement.
  //
  // Fix: a link click closes the dialog synchronously here, in the same
  // tick as the click, and skips the scroll-position restore entirely
  // (isNavigating, read by the 'close' handler above). dialog.close()
  // fires the native 'close' event synchronously, so the unlock has
  // already happened by the time the browser performs the anchor's own
  // default scroll -- verified live that a fragment target then reaches
  // its correct position. No preventDefault anywhere: these stay real
  // anchors (same-page jump, or a genuine cross-page navigation back to
  // the Homepage from Contact or Programme) that keep working with
  // JavaScript disabled -- with a scroll lock is never even engaged in
  // that case, since opening the panel itself requires JS.
  for (var i = 0; i < links.length; i++) {
    links[i].addEventListener('click', function () {
      if (!dialog.open) return;
      isNavigating = true;
      dialog.close();
    });
  }

  function wireTriggers() {
    var triggers = document.querySelectorAll('[data-silensia-page-index-trigger]');
    for (var i = 0; i < triggers.length; i++) {
      var trigger = triggers[i];
      if (trigger.dataset.silensiaPageIndexWired) continue;
      trigger.dataset.silensiaPageIndexWired = 'true';
      trigger.addEventListener('click', function (event) {
        var target = event.currentTarget;
        if (dialog.open) {
          beginClose();
        } else {
          openPanel(target);
        }
      });
    }
  }

  wireTriggers();

  // Focus the destination heading after a same-page or cross-page anchor
  // arrival, so keyboard and assistive-tech users land on real content, not
  // merely a scroll position. Fails safely when the target is absent (e.g.
  // Contact, which carries none of the five page-index ids).
  function focusAnchorTarget(hash) {
    if (!hash) return;
    var id = hash.replace('#', '');
    var target = document.getElementById(id);
    if (!target) return;
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    window.requestAnimationFrame(function () {
      target.focus({ preventScroll: true });
    });
  }

  window.addEventListener('hashchange', function () {
    focusAnchorTarget(window.location.hash);
  });

  if (window.location.hash) {
    focusAnchorTarget(window.location.hash);
  }

  // ---------------------------------------------------------------------
  // Explore trigger visibility (Homepage or Programme -- 2026-08-28) --
  // the same two-flag IntersectionObserver pattern already proven by the
  // floating Contact CTA (assets/silensia-homepage-interactions.js,
  // initFloatingCta()), reading whichever of the two pages' own opening/
  // closing targets are actually present, exactly the same generalisation
  // that function itself uses.
  // ---------------------------------------------------------------------
  // The floating Explore trigger this function used to show and hide no
  // longer exists -- it was replaced by the always-present section rail
  // (Silensia V2 comments.pdf, page 12), which needs no visibility
  // choreography. Kept as a no-op guard rather than deleted so any
  // remaining call site stays harmless.
  function initExploreVisibility() {
    return;
  }

  initExploreVisibility();

  // ---------------------------------------------------------------------
  // Active-section indication -- one IntersectionObserver, restrained
  // aria-current on every matching [data-target] link, no continuous
  // scroll polling. Generalised 2026-08-25 (Programme's own redesigned
  // six-area index, sections/silensia-programme-content.liquid) from
  // dialog-only to document-wide: any element anywhere on the page
  // carrying data-target="<section id>" now receives aria-current in
  // lockstep with the shared page-index panel's own links, driven by this
  // one observer -- not a second navigation script for the same state.
  // ---------------------------------------------------------------------
  // ---------------------------------------------------------------------
  // Rail contrast over dark surfaces.
  //
  // The section rail is fixed to the right edge, vertically centred, and
  // set in Obsidian -- correct over the Ivory chapters, invisible over the
  // dark ones (the Hero at the top, and the closing conversation panel and
  // contact panel at the bottom, plus the footer).
  //
  // The root margin below narrows the observer's viewport to a thin band
  // centred on 50% -- the rail's own vertical position -- so the switch
  // fires the moment a dark surface actually reaches the rail, not when
  // it merely enters the screen.
  //
  // The band is 2% tall rather than a zero-height line at exactly 50%.
  // That was the first attempt and it silently never fired: an element
  // crossing a zero-height root produces a zero-AREA intersection, which
  // does not count as intersecting at threshold 0. Confirmed live -- the
  // rail stayed Obsidian over the Hero. A thin band has real area and
  // behaves identically for this purpose. Sections opt in by
  // carrying data-silensia-dark-surface in their own Liquid, so this list
  // never has to be maintained here.
  //
  // A SET of the surfaces currently under the rail, not a boolean and not
  // a counter. A boolean is wrong because two dark surfaces can touch the
  // band at once (the contact panel and the footer meet there), and it
  // would clear on the first one leaving while the second is still under
  // the rail. A counter was the first attempt and was also wrong: the
  // observer's very first callback delivers an entry for EVERY observed
  // element, including the ones that are not intersecting, so the three
  // non-intersecting entries decremented away the one real hit and the
  // rail never switched over the Hero. A set only ever reflects current
  // membership, so it cannot drift out of step with reality.
  // ---------------------------------------------------------------------
  function initRailContrast() {
    var rail = document.querySelector('.silensia-page-rail');
    if (!rail) return;
    var darkSurfaces = document.querySelectorAll('[data-silensia-dark-surface]');
    if (!darkSurfaces.length) return;

    // No IntersectionObserver: leave the rail in its default state rather
    // than guessing. It stays legible over the Ivory chapters, which is
    // most of the page.
    if (!('IntersectionObserver' in window)) return;

    var active = [];
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var at = active.indexOf(entry.target);
          if (entry.isIntersecting) {
            if (at === -1) active.push(entry.target);
          } else if (at !== -1) {
            active.splice(at, 1);
          }
        });
        rail.classList.toggle('is-on-dark', active.length > 0);
      },
      { rootMargin: '-49% 0px -49% 0px', threshold: 0 }
    );

    for (var i = 0; i < darkSurfaces.length; i++) {
      observer.observe(darkSurfaces[i]);
    }
  }

  function initActiveSection() {
    if (!('IntersectionObserver' in window)) return;
    // Homepage's five chapter ids plus Programme's six area ids in one
    // list -- document.getElementById() returns null harmlessly for
    // whichever set the current page doesn't render, so only the ids
    // actually present end up observed, with no page_type branching
    // needed here either.
    // Corrected 2 September 2026: this list still named four Homepage
    // section ids that no longer exist anywhere in the theme ('villa',
    // 'programme', 'day', 'journey'), left over from the pre-refactor
    // chapter set. getElementById returned null for all four, so on the
    // Homepage only '#philosophy' was ever observed and the active-section
    // state effectively never advanced. These are the ids the Homepage
    // sections actually render today, and they are the same ids the
    // navigation links point at.
    var sectionIds = [
      'philosophy', 'what-we-treat', 'how-we-treat', 'expertise', 'residences',
      'medical', 'psychological', 'therapeutic', 'nutritional', 'physical', 'lifestyle',
      'coordination', 'admissions', 'villa', 'questions'
    ];
    var sections = [];
    for (var i = 0; i < sectionIds.length; i++) {
      var section = document.getElementById(sectionIds[i]);
      if (section) sections.push(section);
    }
    if (!sections.length) return;

    // Stale-state fix, 7 September 2026.
    //
    // The previous callback only ever ADDED aria-current, on the early
    // `if (!entry.isIntersecting) return;`. Nothing ever removed it when
    // the last tracked section left the band, so scrolling past
    // #residences into the FAQ, the contact panel and the footer left
    // "Residences" marked as the current section for the whole rest of
    // the page -- pointing at content that was nowhere on screen.
    //
    // Same shape as initRailContrast() above, and for the same reason: a
    // SET of what is currently in the band, recomputed on every callback,
    // so the state cannot drift out of step with reality. When the set
    // empties, every aria-current is cleared and no section is current --
    // which is the honest answer over the footer.
    //
    // When two adjacent sections both cross the band (it is 10% of the
    // viewport tall, so this happens at every boundary), the later one in
    // document order wins. Scrolling down that is the section being
    // entered; scrolling up it holds the lower section until it genuinely
    // leaves the band. Deterministic either way, which the previous
    // entries.forEach() ordering was not.
    var inBand = [];

    function applyCurrent() {
      var current = null;
      for (var i = 0; i < sections.length; i++) {
        if (inBand.indexOf(sections[i]) !== -1) current = sections[i];
      }

      var allTargetLinks = document.querySelectorAll('[data-target]');
      for (var j = 0; j < allTargetLinks.length; j++) {
        allTargetLinks[j].removeAttribute('aria-current');
      }

      if (!current) return;

      var matchingLinks = document.querySelectorAll('[data-target="' + current.id + '"]');
      for (var k = 0; k < matchingLinks.length; k++) {
        matchingLinks[k].setAttribute('aria-current', 'location');
      }
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var at = inBand.indexOf(entry.target);
          if (entry.isIntersecting) {
            if (at === -1) inBand.push(entry.target);
          } else if (at !== -1) {
            inBand.splice(at, 1);
          }
        });
        applyCurrent();
      },
      // The band moved up on 7 September 2026, from 40%-50% of the
      // viewport to 15%-25%. It used to sit on the vertical middle, so a
      // section only became current once its top had travelled halfway up
      // the screen -- by which point the reader had been inside it for
      // most of a viewport height and the rail was reporting the section
      // above. Near the top, the rail changes as a section's opening
      // actually arrives, which is what a progress indicator is for.
      //
      // Still 10% tall, and still below the header, so a section is never
      // marked current while its heading is behind the fixed header.
      { rootMargin: '-15% 0px -75% 0px', threshold: 0 }
    );

    sections.forEach(function (section) {
      observer.observe(section);
    });
  }

  initActiveSection();
  initRailContrast();

  // Theme Editor: a header re-render (settings change) replaces the Menu
  // trigger node, which needs re-wiring; the wired-flag check above keeps
  // this idempotent for every other, still-connected trigger. A section
  // reload can also fire mid-close (or while the dialog is genuinely open),
  // which would otherwise strand the scroll lock forever, since a replaced
  // section can drop the in-flight close's own setTimeout callback along
  // with it -- reconcile defensively: if the dialog itself is no longer
  // open but the lock is still applied, release it.
  document.addEventListener('shopify:section:load', function () {
    wireTriggers();
    if (!dialog.open && scrollLockTarget.style.overflow === 'hidden') {
      unlockScroll();
    }
  });
})();
